/* eslint-disable no-undef */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback } from "react";
import "./css/StaffPage.css";
import { getMyBdePerformance } from "../api/careerApi";
import {
  FiUsers,
  FiCheckCircle,
  FiClock,
  FiCopy,
  FiLink,
} from "react-icons/fi";
import { BsCurrencyRupee } from "react-icons/bs";
import AccessControl from "../components/AccessControl";

export default function BdeDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: "" });

  const showToast = (message) => {
    setToast({ show: true, message });
    setTimeout(() => setToast({ show: false, message: "" }), 2500);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMyBdePerformance();
      if (res.success) setData(res);
      else setData(null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const copyInviteLink = async () => {
    if (!data?.inviteLink) return;
    try {
      await navigator.clipboard.writeText(data.inviteLink);
      showToast("Invite link copied!");
    } catch {
      showToast(data.inviteLink);
    }
  };

  const STATUS_META = {
    applied: { label: "Applied", color: "#854F0B", bg: "#FAEEDA" },
    verification_in_progress: { label: "Verifying", color: "#854F0B", bg: "#FAEEDA" },
    waiting: { label: "Waiting", color: "#854F0B", bg: "#FAEEDA" },
    approved: { label: "Approved", color: "#0F6E56", bg: "#E1F5EE" },
    rejected: { label: "Rejected", color: "#A32D2D", bg: "#FCEBEB" },
    online: { label: "Online", color: "#0F6E56", bg: "#E1F5EE" },
    offline: { label: "Offline", color: "#555", bg: "#F1EFE8" },
    confirmed: { label: "On a Ride", color: "#185FA5", bg: "#E6F1FB" },
    trip_started: { label: "On a Ride", color: "#185FA5", bg: "#E6F1FB" },
    trip_completed: { label: "Trip Done", color: "#0F6E56", bg: "#E1F5EE" },
    canceled: { label: "Canceled", color: "#A32D2D", bg: "#FCEBEB" },
  };

  if (loading) {
    return (
      <div>
        <AccessControl allowedRoles={["SUPERADMIN","ADMIN","BDE"]} redirectTo="/" />
        <div className="sf-loading" style={{ height: "60vh" }}>
          <div
            className="spinner"
            style={{ width: 24, height: 24, borderColor: "#1d9e75", borderTopColor: "transparent" }}
          ></div>
          <p style={{ marginTop: 12, color: "#888", fontSize: 13 }}>Loading your dashboard…</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div>
        <AccessControl allowedRoles={["SUPERADMIN","ADMIN","BDE"]} redirectTo="/" />
        <div className="sf-empty">
          <FiUsers size={40} color="#ddd" />
          <p>Couldn't load your onboarding data. Try refreshing.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <AccessControl allowedRoles={["SUPERADMIN","ADMIN","BDE"]} redirectTo="/" />

      {toast.show && (
        <div className="sf-toast sf-toast-success">
          <span><FiCopy size={16} /></span>
          <span>{toast.message}</span>
        </div>
      )}

      {/* Header */}
      <div className="sf-page-header section">
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: "#1a1a1a", margin: 0 }}>
            Welcome, {data.staff?.name} 👋
          </h2>
          <p style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
            Your driver onboarding & incentive summary
          </p>
        </div>
      </div>

      {/* Invite link card */}
      <div className="card section" style={{ padding: "16px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#555", display: "flex", alignItems: "center", gap: 6 }}>
              <FiLink size={13} /> Your Driver Invite Link
            </div>
            <div style={{ fontSize: 13, fontFamily: "monospace", color: "#185FA5", marginTop: 4, wordBreak: "break-all" }}>
              {data.inviteLink}
            </div>
          </div>
          <button className="btn btn-success" onClick={copyInviteLink}>
            <FiCopy size={13} /> Copy Link
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="stat-grid section">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: "#E6F1FB" }}>
            <FiUsers size={18} color="#185FA5" />
          </div>
          <div className="stat-label">Total Drivers Onboarded</div>
          <div className="stat-value">{data.totalOnboarded}</div>
          <div className="stat-change neutral">Via your invite link</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: "#E1F5EE" }}>
            <FiCheckCircle size={18} color="#0F6E56" />
          </div>
          <div className="stat-label">First Ride Completed</div>
          <div className="stat-value" style={{ color: "#0F6E56" }}>{data.incentivizedDrivers}</div>
          <div className="stat-change up">Incentive credited</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: "#FAEEDA" }}>
            <FiClock size={18} color="#854F0B" />
          </div>
          <div className="stat-label">Awaiting First Ride</div>
          <div className="stat-value" style={{ color: "#854F0B" }}>{data.pendingFirstRide}</div>
          <div className="stat-change neutral">Not yet earned incentive</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: "#EEEDFE" }}>
            <BsCurrencyRupee size={16} color="#3C3489" />
          </div>
          <div className="stat-label">Total Incentive Earned</div>
          <div className="stat-value" style={{ color: "#3C3489" }}>₹{data.totalIncentiveEarned}</div>
          <div className="stat-change up">₹{data.incentivePerDriver} per driver's first ride</div>
        </div>
      </div>

      {/* Drivers table */}
      <div className="card section">
        <div style={{ padding: "14px 20px 0", fontSize: 14, fontWeight: 600, color: "#1a1a1a" }}>
          Drivers You've Onboarded
        </div>
        {data.drivers?.length === 0 ? (
          <div className="sf-empty">
            <FiUsers size={40} color="#ddd" />
            <p>No drivers onboarded yet — share your invite link to get started</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="sf-table">
              <thead>
                <tr>
                  <th>Driver</th>
                  <th>Vehicle</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th>Incentive</th>
                </tr>
              </thead>
              <tbody>
                {data.drivers?.map((d) => {
                  const meta = STATUS_META[d.status] || { label: d.status, color: "#555", bg: "#F1EFE8" };
                  return (
                    <tr key={d._id}>
                      <td>
                        <div className="sf-member-cell">
                          <div className="sf-avatar">{d.name?.charAt(0)?.toUpperCase() || "D"}</div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>{d.name || "—"}</div>
                            <div style={{ fontSize: 11, color: "#888" }}>{d.phone}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ fontSize: 12, color: "#555" }}>{d.vehicleNumber || "—"}</td>
                      <td style={{ fontSize: 12, color: "#555", textTransform: "capitalize" }}>{d.type}</td>
                      <td>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            padding: "4px 10px",
                            borderRadius: 20,
                            color: meta.color,
                            background: meta.bg,
                          }}
                        >
                          {meta.label}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: "#555" }}>
                        {d.createdAt ? new Date(d.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                      </td>
                      <td>
                        {d.firstRideIncentiveCredited ? (
                          <span style={{ fontSize: 12, fontWeight: 700, color: "#0F6E56" }}>
                            ✓ ₹100 Earned
                          </span>
                        ) : (
                          <span style={{ fontSize: 12, color: "#aaa" }}>Pending first ride</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}